import { ToBillArguments, ToBillEvent } from './Factura';

export type CfdiIndexKind = 'master' | 'billEvent' | 'notaCredito' | 'pago' | 'orphan';
export type CfdiIndexStatus = 'vigente' | 'cancelando' | 'cancelada' | 'sustituida';
export type CfdiTipoDeComprobante = 'I' | 'E' | 'P' | 'T' | 'N';

/** One row of the CFDI glossary served by getCfdiIndexPage */
export interface CfdiIndexEntry {
    uuid: string;
    created: number;                    // ms
    tipoDeComprobante: CfdiTipoDeComprobante;
    isPayment: boolean;
    kind: CfdiIndexKind;
    serie?: string;
    folio?: string;
    receptorRfc: string;
    receptorNombre: string;
    usoCFDI?: string;
    total: number;
    moneda?: string;
    metodoPago?: string;
    formaPago?: string;
    status: CfdiIndexStatus;
    replacesUuid?: string;
    replacedByUuid?: string;
    argumentId?: string;
    eventId?: string;
    multipayType?: 0 | 1 | 2 | 3;
    specialType?: 'cartaporte' | 'nomina' | null;
    paymentNum?: number;
    sandbox?: boolean;
    /** Computed by the back-end per request: other CFDIs related to this one (pagos, notas, sustituciones) */
    relatedCfdis?: number;
}

export interface CfdiIndexPage {
    items: CfdiIndexEntry[];
    total: number;
    page: number;
    pageSize: number;
}

export interface CfdiIndexGroup {
    argument: ToBillArguments<ToBillEvent>;
    cfdis: CfdiIndexEntry[];
}

/** Mirrors the back-end `CfdiIndexFilter` input. All fields optional. */
export interface CfdiIndexFilter {
    start?: number;
    end?: number;
    rfc?: string;
    nombre?: string;
    uuid?: string;
    serie?: string;
    folio?: string;
    status?: CfdiIndexStatus;
    tipoDeComprobante?: CfdiTipoDeComprobante;
    kind?: CfdiIndexKind;
    multipayType?: 0 | 1 | 2 | 3;
    specialType?: 'cartaporte' | 'nomina';
}

export const CFDI_STATUS_LABEL: Record<CfdiIndexStatus, string> = {
    vigente: 'Vigente',
    cancelando: 'Cancelando',
    cancelada: 'Cancelada',
    sustituida: 'Sustituida',
};

export const CFDI_TIPO_LABEL: Record<CfdiTipoDeComprobante, string> = {
    I: 'Factura',
    E: 'Nota de crédito',
    P: 'Complemento de pago',
    T: 'Traslado',
    N: 'Nómina',
};

export const CFDI_KIND_LABEL: Record<CfdiIndexKind, string> = {
    master: 'Factura principal',
    billEvent: 'Factura relacionada',
    notaCredito: 'Nota de crédito',
    pago: 'Complemento de pago',
    orphan: 'Sin relación',
};

// ---------- URL query-string (de)serialization, shared by the list and reports views ----------

const FILTER_KEYS: (keyof CfdiIndexFilter)[] = ['start', 'end', 'rfc', 'nombre', 'uuid', 'serie', 'folio', 'status', 'tipoDeComprobante', 'kind', 'multipayType', 'specialType'];
const NUMERIC_KEYS: (keyof CfdiIndexFilter)[] = ['start', 'end', 'multipayType'];

export const filterToSearchParams = (filter: CfdiIndexFilter, extra: Record<string, string | number | undefined | null> = {}): string => {
    const params = new URLSearchParams();
    FILTER_KEYS.forEach(key => {
        const value = filter[key];
        if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    });
    Object.entries(extra).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    });
    const str = params.toString();
    return str ? `?${str}` : '';
};

export const filterFromSearch = (search: string): { filter: CfdiIndexFilter; extra: URLSearchParams } => {
    const params = new URLSearchParams(search || '');
    const filter: CfdiIndexFilter = {};
    FILTER_KEYS.forEach(key => {
        const raw = params.get(key);
        if (raw === null || raw === '') return;
        if (NUMERIC_KEYS.includes(key)) {
            const n = parseInt(raw, 10);
            if (!isNaN(n)) (filter as any)[key] = n;
        } else {
            (filter as any)[key] = raw;
        }
    });
    return { filter, extra: params };
};

/** Removes empty keys so Apollo variables and URL stay tidy */
export const compactFilter = (filter: CfdiIndexFilter): CfdiIndexFilter => {
    const out: CfdiIndexFilter = {};
    FILTER_KEYS.forEach(key => {
        const value = filter[key];
        if (value !== undefined && value !== null && value !== '') (out as any)[key] = value;
    });
    return out;
};
